import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Sparkles, ListChecks, BookOpen, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const KINDS = [
  { key: 'numeric_range', label: 'Bin: numeric',  hint: 'Split a number field into ranges (e.g. 30/60/90 aging)' },
  { key: 'date_range',    label: 'Bin: date',     hint: 'Split a date field into buckets (this month / last quarter / older)' },
  { key: 'value_list',    label: 'Group: values', hint: 'Bucket a text field by explicit member lists (US,CA → Americas)' },
  { key: 'custom_rule',   label: 'Group: rules',  hint: 'Free-form predicates over one or more fields' },
];

const emptyBucket = (i) => ({ label: `Bucket ${i + 1}`, from: '', to: '', values: [], predicate: '', sort: i + 1 });

/**
 * Reusable "Bins & Groups" editor. Emits a bucket_spec on every edit and
 * exposes an onChange({spec, valid}) callback so the enclosing dialog
 * can gate its create/save button.
 *
 * Props:
 *   modelId, entityName, fields, fieldsNumeric, fieldsDate, fieldsText
 *   defaultBaseField?
 *   value?      — controlled bucket_spec (optional)
 *   onChange({spec, valid, formula, sortExpr, warnings})
 *   testIdPrefix?  — used to namespace data-testid attributes
 */
export function BucketEditor({
  modelId, entityName, fields, fieldsNumeric, fieldsDate, fieldsText,
  defaultBaseField = '', value, onChange, testIdPrefix = 'bucket',
}) {
  const [kind, setKind] = useState(value?.kind || 'numeric_range');
  const [baseField, setBaseField] = useState(value?.base_field || defaultBaseField || '');
  const [buckets, setBuckets] = useState(value?.buckets || [emptyBucket(0), emptyBucket(1)]);
  const [fallback, setFallback] = useState(value?.fallback_label || 'Other');
  const [presets, setPresets] = useState([]);
  const [compileResult, setCompileResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [compileError, setCompileError] = useState('');

  const eligibleFields = useMemo(() => {
    if (kind === 'numeric_range') return fieldsNumeric || [];
    if (kind === 'date_range') return fieldsDate || [];
    if (kind === 'value_list') return fieldsText || [];
    return fields || [];
  }, [kind, fields, fieldsNumeric, fieldsDate, fieldsText]);

  // Load presets once.
  useEffect(() => {
    api.getBucketPresets()
      .then(r => setPresets(r.presets || []))
      .catch(() => {});
  }, []);

  // Emit changes upstream + compile.
  useEffect(() => {
    const spec = {
      kind, base_field: baseField, fallback_label: fallback,
      buckets: buckets.map((b, i) => {
        const out = { label: b.label, sort: b.sort ?? i + 1 };
        if (kind === 'numeric_range' || kind === 'date_range') {
          out.from = b.from === '' || b.from === null ? null : (kind === 'numeric_range' ? Number(b.from) : String(b.from));
          out.to   = b.to   === '' || b.to   === null ? null : (kind === 'numeric_range' ? Number(b.to)   : String(b.to));
        } else if (kind === 'value_list') {
          out.values = Array.isArray(b.values) ? b.values : String(b.values || '').split(',').map(s => s.trim()).filter(Boolean);
        } else {
          out.predicate = b.predicate || '';
        }
        return out;
      }),
    };

    const valid = (kind === 'custom_rule' || !!baseField) &&
                  spec.buckets.length > 0 &&
                  spec.buckets.every(b => b.label && b.label.trim() &&
                    (kind === 'value_list' ? (b.values || []).length > 0
                    : kind === 'custom_rule' ? !!b.predicate
                    : (b.from !== null || b.to !== null)));

    let cancelled = false;
    const doCompile = async () => {
      if (!valid) {
        setCompileResult(null);
        setCompileError('');
        onChange && onChange({ spec, valid: false });
        return;
      }
      try {
        const c = await api.compileBucketSpec(spec);
        if (cancelled) return;
        setCompileResult(c);
        setCompileError('');
        onChange && onChange({ spec, valid: true, formula: c.formula_expr, sortExpr: c.sort_expr, warnings: c.warnings });
      } catch (e) {
        if (cancelled) return;
        setCompileResult(null);
        setCompileError(e.message || 'Compile failed');
        onChange && onChange({ spec, valid: false });
      }
    };
    const t = setTimeout(doCompile, 300); // debounce
    return () => { cancelled = true; clearTimeout(t); };
  }, [kind, baseField, JSON.stringify(buckets), fallback]);

  const updBucket = (i, patch) => setBuckets(bs => bs.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const addBucket = () => setBuckets(bs => [...bs, emptyBucket(bs.length)]);
  const rmBucket = (i) => setBuckets(bs => bs.filter((_, idx) => idx !== i));

  const applyPreset = (id) => {
    const p = presets.find(x => x.id === id);
    if (!p) return;
    setKind(p.kind);
    setBuckets(p.buckets.map((b, i) => ({ ...b, from: b.from ?? '', to: b.to ?? '', sort: b.sort ?? i + 1, values: b.values || [], predicate: b.predicate || '' })));
    setFallback(p.fallback_label || 'Other');
  };

  const doSuggest = async () => {
    if (!baseField) {
      toast.error('Pick a base field first, then AI Suggest can read its sample values.');
      return;
    }
    setSuggesting(true);
    try {
      const r = await api.suggestBucketSpec({ model_id: modelId, entity_name: entityName, field_name: baseField });
      const s = r.spec || {};
      setKind(s.kind || 'numeric_range');
      setBuckets((s.buckets || []).map((b, i) => ({ ...b, from: b.from ?? '', to: b.to ?? '', sort: b.sort ?? i + 1, values: b.values || [], predicate: b.predicate || '' })));
      setFallback(s.fallback_label || 'Other');
      toast.success(`AI suggested ${(s.buckets || []).length} buckets from ${r.sample_size || 0} samples`);
    } catch (e) {
      toast.error(e.message || 'AI suggest failed');
    } finally {
      setSuggesting(false);
    }
  };

  const runPreview = useCallback(async () => {
    if (!baseField || !modelId) return;
    try {
      const spec = {
        kind, base_field: baseField, fallback_label: fallback,
        buckets: buckets.map((b, i) => {
          const out = { label: b.label, sort: b.sort ?? i + 1 };
          if (kind === 'numeric_range' || kind === 'date_range') {
            out.from = b.from === '' || b.from === null ? null : (kind === 'numeric_range' ? Number(b.from) : b.from);
            out.to   = b.to   === '' || b.to   === null ? null : (kind === 'numeric_range' ? Number(b.to)   : b.to);
          } else if (kind === 'value_list') {
            out.values = Array.isArray(b.values) ? b.values : String(b.values || '').split(',').map(s => s.trim()).filter(Boolean);
          }
          return out;
        }),
      };
      const r = await api.previewBucketSpec({ model_id: modelId, entity_name: entityName, bucket_spec: spec });
      setPreview(r);
    } catch (e) {
      setPreview(null);
    }
  }, [modelId, entityName, baseField, kind, fallback, buckets]);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-editor`}>
      {/* Kind selector */}
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map(k => (
          <button
            key={k.key}
            data-testid={`${testIdPrefix}-kind-${k.key}`}
            onClick={() => setKind(k.key)}
            className={`px-3 py-1 rounded-full text-[11px] border transition ${
              kind === k.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-transparent text-muted-foreground border-border/50 hover:border-primary/40'
            }`}
            title={k.hint}
          >{k.label}</button>
        ))}
      </div>

      {/* Base field + AI + Presets */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Base field</Label>
          {kind === 'custom_rule' ? (
            <div className="text-[11px] text-muted-foreground/70 mt-1 italic">Rules can reference any fields directly.</div>
          ) : (
            <Select value={baseField} onValueChange={setBaseField}>
              <SelectTrigger className="h-8 text-sm mt-0.5 rounded-lg bg-muted/50 border-border/50" data-testid={`${testIdPrefix}-base-field`}>
                <SelectValue placeholder="Choose a field" />
              </SelectTrigger>
              <SelectContent>
                {eligibleFields.length === 0 && <SelectItem value="__none" disabled>No eligible fields</SelectItem>}
                {eligibleFields.map(f => <SelectItem key={f.name} value={f.name}>{f.display_name || f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-end gap-1.5">
          <Button
            variant="outline" size="sm" onClick={doSuggest} disabled={suggesting || !baseField || kind === 'custom_rule'}
            className="h-8 text-[11px] gap-1.5 flex-1" data-testid={`${testIdPrefix}-ai-suggest`}
          >
            <Sparkles className={`w-3 h-3 ${suggesting ? 'animate-pulse text-amber-400' : ''}`} />
            {suggesting ? 'Analyzing…' : 'AI Suggest'}
          </Button>
          {presets.length > 0 && (
            <Select onValueChange={applyPreset}>
              <SelectTrigger className="h-8 text-[11px] w-32" data-testid={`${testIdPrefix}-presets`}>
                <BookOpen className="w-3 h-3 mr-1" />
                <SelectValue placeholder="Presets" />
              </SelectTrigger>
              <SelectContent>
                {presets.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    <div className="flex flex-col">
                      <span>{p.label}</span>
                      <span className="text-[9px] text-muted-foreground">{p.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Bucket rows */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-muted-foreground">Buckets</Label>
          <Button size="sm" variant="ghost" onClick={addBucket} className="h-6 text-[10px] gap-1" data-testid={`${testIdPrefix}-add-row`}>
            <Plus className="w-3 h-3" /> Add bucket
          </Button>
        </div>
        {buckets.map((b, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`${testIdPrefix}-row-${i}`}>
            <Input
              value={b.label} onChange={e => updBucket(i, { label: e.target.value })}
              placeholder="Label" className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1 min-w-0"
              data-testid={`${testIdPrefix}-row-${i}-label`}
            />
            {(kind === 'numeric_range' || kind === 'date_range') && (
              <>
                <Input
                  value={b.from ?? ''} onChange={e => updBucket(i, { from: e.target.value })}
                  placeholder={kind === 'date_range' ? 'YYYY-MM-DD' : 'from'}
                  className="h-7 text-[11px] rounded bg-muted/40 border-border/40 w-24"
                  data-testid={`${testIdPrefix}-row-${i}-from`}
                />
                <span className="text-[10px] text-muted-foreground/80">→</span>
                <Input
                  value={b.to ?? ''} onChange={e => updBucket(i, { to: e.target.value })}
                  placeholder={kind === 'date_range' ? 'YYYY-MM-DD' : 'to'}
                  className="h-7 text-[11px] rounded bg-muted/40 border-border/40 w-24"
                  data-testid={`${testIdPrefix}-row-${i}-to`}
                />
              </>
            )}
            {kind === 'value_list' && (
              <Input
                value={Array.isArray(b.values) ? b.values.join(', ') : b.values}
                onChange={e => updBucket(i, { values: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="US, CA, MX" className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1"
                data-testid={`${testIdPrefix}-row-${i}-values`}
              />
            )}
            {kind === 'custom_rule' && (
              <Input
                value={b.predicate || ''} onChange={e => updBucket(i, { predicate: e.target.value })}
                placeholder="amount > 10000 and status = 'open'"
                className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1 font-mono"
                data-testid={`${testIdPrefix}-row-${i}-predicate`}
              />
            )}
            <button onClick={() => rmBucket(i)} className="p-1 text-muted-foreground/70 hover:text-rose-400" data-testid={`${testIdPrefix}-row-${i}-remove`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Fallback */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Fallback (unmatched values)</Label>
          <Input
            value={fallback} onChange={e => setFallback(e.target.value)}
            className="h-7 text-[11px] rounded bg-muted/40 border-border/40 mt-0.5"
            data-testid={`${testIdPrefix}-fallback`}
          />
        </div>
        <div className="flex items-end">
          <Button size="sm" variant="ghost" onClick={runPreview} disabled={!baseField} className="h-7 text-[10px] w-full gap-1" data-testid={`${testIdPrefix}-preview-btn`}>
            <ListChecks className="w-3 h-3" /> Preview counts
          </Button>
        </div>
      </div>

      {/* Preview */}
      {preview?.available && (preview.counts || []).length > 0 && (
        <div className="rounded-md border border-border/40 bg-muted/20 p-2" data-testid={`${testIdPrefix}-preview`}>
          <div className="text-[10px] text-muted-foreground mb-1">Preview using {preview.sample_size} sample values</div>
          <div className="flex flex-wrap gap-1">
            {preview.counts.map(c => (
              <span key={c.label} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                {c.label}: <b>{c.count}</b>
              </span>
            ))}
          </div>
        </div>
      )}
      {preview && !preview.available && (
        <div className="text-[10px] text-muted-foreground/70 italic">{preview.reason}</div>
      )}

      {/* Warnings + compile status */}
      {compileError && (
        <div className="flex items-start gap-1.5 text-[10px] text-rose-400" data-testid={`${testIdPrefix}-error`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {compileError}
        </div>
      )}
      {(compileResult?.warnings || []).map((w, i) => (
        <div key={i} className="flex items-start gap-1.5 text-[10px] text-amber-500">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {w}
        </div>
      ))}
      {compileResult && !compileError && (
        <div className="text-[9px] text-muted-foreground/70">Compiled: <code className="text-primary/80">{(compileResult.formula_expr || '').slice(0, 120)}{(compileResult.formula_expr || '').length > 120 ? '…' : ''}</code></div>
      )}
    </div>
  );
}
